import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useForm } from "react-hook-form";
import { ArrowPathIcon, CloudArrowUpIcon } from "@heroicons/react/24/outline";
import TextArea from "../../../atomic/TextArea";
import Checkbox from "../../../composed/FormElements/Checkbox";

import { onConfirm } from "../helper";
import RejectionReasonSelector from "../../RejectionReasonSelector";
import DropdownWithSearch from "../../../../../../src/components/atomic/DropdownWithSearch";
import type { DropdownWithSearchOption } from "../../../../../../src/components/atomic/DropdownWithSearch/interface";

import { LoanApplicationService } from "../../../../services/LoanApplication";
import type { NotifyStatesTypes } from "../../../../components/atomic/Notification";
import { useSmartLoanNavigation } from "../../../atomic/SmartLoanLink";
import { useParams } from "react-router";
import FileUploadInput from "../../../../components/composed/FormElements/FileUploadInput";
import { FileUploadRejectionReason } from "../../../../components/composed/FormElements/constants";
import { CommonService } from "src/services/Common";
import { getCurrentLender } from "src/shared/utils/Global";
import { LENDER_NAMES } from "src/shared/utils/constants";
import { getNotificationText } from "src/shared/utils/TextUtil";
import UploadedDocumentView from "../../../../components/composed/UploadedDocumentView";
import { DOCUMENT_IDS } from "src/shared/utils/constants";
interface IProps {
  setOpen: (open: boolean) => void;
  reload: () => void;
  loanApplicationID: string;
  pageName: string;
  notify: (args: {
    title: ReactNode;
    type?: NotifyStatesTypes;
    text?: ReactNode;
  }) => void;
  token: string;
  taskID: string;
  workflowInstanceID: string;
  isLenderDashboard: boolean;
  setShowCaseMovedModal: (show: boolean) => void;
  userID: string;
}

interface WorkflowActionResponse {
  status: boolean;
  data?: {
    action?: Record<string, string>;
  };
}

export default function NextStage({
  setOpen,
  loanApplicationID = "",
  pageName = "",
  notify,
  token,
  taskID = "",
  workflowInstanceID = "",
  isLenderDashboard,
  setShowCaseMovedModal,
  userID
}: IProps) {
  const { handleSubmit, register, control, setValue, watch } = useForm();
  const file = watch("file");
  const uploadedFile = watch("file");
  const { navigateToLoanApplication } = useSmartLoanNavigation();
  const maxFilesAllowed = 5;
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingActions, setIsLoadingActions] = useState(true);
  const [allTaskItemsCompleted, setAllTaskItemsCompleted] = useState(false);
  const [actions, setActions] = useState<DropdownWithSearchOption[]>([]);
  const [selectedStage, setSelectedStage] =
    useState<DropdownWithSearchOption | null>(null);
  const [reason, setReason] = useState<{
    rejectionReason: string;
    subRejectionReason: string;
  } | null>(null);
  const [object, setObject] = useState<File | null>(null);

  const lender = getCurrentLender();
  const isHERO_FINCORP = lender === LENDER_NAMES.HFCL;

  const getRejectedKey = useMemo(
    () =>
      actions.length > 0
        ? actions.find((action) => action?.value?.includes("reject"))?.value
        : "",
    [actions]
  );

  const params = useParams();

  let sourceEntityID: string | undefined = "";
  if (!isLenderDashboard) {
    sourceEntityID = (params as { sourceEntityID: string }).sourceEntityID;
  }

  useEffect(() => {
    const getWorkflowAction = async () => {
      setIsLoadingActions(true);
      try {
        const response = await LoanApplicationService.getWorkflowReqData({
          token: token,
          pageName: pageName as string,
          taskId: taskID!,
          workflowInstanceId: workflowInstanceID!,
          isLenderDashboard,
          loanApplicationID,
          sourceEntityId: sourceEntityID
        });

        const typed: WorkflowActionResponse =
          response as WorkflowActionResponse;

        if (typed?.status && typed?.data?.action) {
          const actionsObj = typed.data.action;
          const actionsArray: DropdownWithSearchOption[] = Object.entries(
            actionsObj
          ).map(([key, value]) => ({
            value: key,
            name: value
          }));

          setActions(actionsArray);
        } else {
          setActions([]);
        }
      } catch (error) {
        console.error("Error fetching workflow actions:", error);
        setActions([]);
      } finally {
        setTimeout(() => {
          setIsLoadingActions(false);
        }, 500);
      }
    };

    getWorkflowAction();
  }, [
    pageName,
    taskID,
    token,
    workflowInstanceID,
    loanApplicationID,
    isLenderDashboard,
    sourceEntityID
  ]);

  async function uploadLenderMedia(fileToUpload: File) {
    const formData = new FormData();

    if (file) formData.append("file", fileToUpload);
    formData.append("mediaType", "Additional_Docs");
    formData.append("userID", userID);

    formData.append("documentID", DOCUMENT_IDS.DECISION);

    const response = await CommonService.uploadDashboardMedia({
      token: token,
      formData: formData as any,
      pageName: pageName,
      isLenderPlatform: isLenderDashboard
    });

    if (response.status) return (response.data as any).mediaID;
    else {
      notify({
        title: "Error",
        type: "error",
        text: response.error
      });
    }
  }

  const uploadDocuments = async () => {
    const selectedFiles: File[] = Array.isArray(file)
      ? file
      : file
        ? [file]
        : [];

    if (selectedFiles.length === 0) return;

    const uploadedMediaIDs: string[] = [];
    const mediaIdsToDocumentIds: Record<string, string> = {};
    const docID = DOCUMENT_IDS.DECISION;

    for (let i = 0; i < selectedFiles.length; i++) {
      const singleFile = selectedFiles[i];

      const mediaID = await uploadLenderMedia(singleFile);
      if (!mediaID) return; // stop if any upload fails

      uploadedMediaIDs.push(mediaID);
      mediaIdsToDocumentIds[mediaID] = docID;
    }

    const payload = {
      mediaIDs: uploadedMediaIDs,
      loanApplicationID,
      mediaIdsToDocumentIds,
      userID
    };

    const response = (await LoanApplicationService.uploadAllDocs({
      pageName: pageName as string,
      token: token,
      payload: payload,
      isPlatformDashboard: !isLenderDashboard,
      sourceEntityID
    })) as any;

    if (response?.status) {
      notify({
        type: "success",
        title: "Success",
        text: getNotificationText(uploadedMediaIDs)
      });
    } else {
      notify({
        type: "error",
        title: "Error",
        text: response.error
      });
    }
  };

  async function onSubmit(formState: { remark?: string }) {
    const isRejectFlow =
      Boolean(selectedStage?.value) && selectedStage?.value === getRejectedKey;

    if (!isHERO_FINCORP && isRejectFlow) {
      const rejectionReason = reason?.rejectionReason?.trim();
      const subRejectionReason = reason?.subRejectionReason?.trim();

      if (!rejectionReason || !subRejectionReason) {
        notify({
          type: "error",
          title: "Error",
          text: "Please select both Rejection Reason and Sub Category"
        });

        return;
      }
    }

    try {
      await uploadDocuments();
      const remark: string = (formState?.remark || "").trim();
      setIsLoading(true);

      const response = await onConfirm(
        loanApplicationID,
        selectedStage?.value as string,
        remark,
        workflowInstanceID,
        token,
        pageName,
        reason?.rejectionReason || "",
        reason?.subRejectionReason || "",
        isLenderDashboard,
        sourceEntityID,
        userID,
        isHERO_FINCORP
      );

      if (response?.status) {
        notify({
          type: "success",
          title: "Success",
          text: "Application moved to next stage successfully"
        });
        setIsLoading(false);
        setShowCaseMovedModal(true);
        setOpen(false);
      } else {
        notify({
          type: "error",
          title: "Error",
          text: response?.error
        });

        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error moving application to next stage:", error);

      notify({
        type: "error",
        title: "Error",
        text: "Something went wrong"
      });

      setIsLoading(false);
    }
  }

  const handleDropRejected = (reason: string) => {
    //Do not clear existing files on rejection (duplicate or over-limit drop). The previously selected files should remain intact.
    // if (!editMode) setValue("file", null)

    if (reason === FileUploadRejectionReason.DUPLICATE) {
      notify({
        type: "error",
        title: "Duplicate File",
        text: "This file has already been selected. Please choose a different file."
      });
    } else if (reason === FileUploadRejectionReason.MAX_FILES) {
      notify({
        type: "error",
        title: "Limit Reached",
        text: `You can attach a maximum of 5 documents.`
      });
    } else {
      notify({
        type: "error",
        title: "File Upload Error",
        text: "There was an error uploading your file. Please try again."
      });
    }
  };

  const isFileUploadDisabled = () => {
    if (Array.isArray(uploadedFile)) {
      return uploadedFile.length >= maxFilesAllowed;
    }

    return !!uploadedFile;
  };

  const renderUploadCustomText = () => (
    <div id="upload-document-text" className="flex flex-col items-center">
      <div className="rounded h-7 w-7 border flex justify-center items-center">
        <CloudArrowUpIcon className="h-5 w-5 text-[#475467]" />
      </div>
      <div className="text-sm mt-2">
        Click to upload
        <span className="text-[#475467]"> or drag and drop</span>
      </div>
      <div className="text-xs text-[#475467] mt-0.5">
        Supported Formats: PDF, PNG, JPG, JPEG
      </div>
    </div>
  );

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-between gap-2">
          <div className="w-1/2">
            {isLoadingActions ? (
              <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Stage
                </label>
                <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 flex items-center justify-center gap-2">
                  <ArrowPathIcon className="w-4 h-4 animate-spin text-gray-500" />
                  <span className="text-gray-500 text-sm">
                    Loading actions...
                  </span>
                </div>
              </div>
            ) : actions.length > 0 ? (
              <DropdownWithSearch
                label="Select Stage"
                requiredIcon
                hasSearch={false}
                options={actions}
                placeholder={"Select Stage"}
                onChange={(data: DropdownWithSearchOption) => {
                  setSelectedStage(data);
                }}
                styles={{
                  container: "!w-full"
                }}
              />
            ) : (
              <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Stage
                </label>
                <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 text-sm">
                  No actions available
                </div>
              </div>
            )}
          </div>

          {selectedStage?.value === "bpo_manager" && (
            <DropdownWithSearch
              label="Select BPO User"
              requiredIcon
              hasSearch={false}
              options={[{ name: "BPO User 1", value: "BPO User 1" }]}
              placeholder={"Select BPO User"}
              onChange={(data: DropdownWithSearchOption) => {
                console.log(data);
              }}
              styles={{
                container: "!w-1/2"
              }}
            />
          )}
        </div>

        {!isHERO_FINCORP &&
          selectedStage?.value &&
          selectedStage.value === getRejectedKey && (
            <RejectionReasonSelector
              pageName={pageName}
              loanApplicationID={loanApplicationID}
              onRejectionReasonChange={setReason}
              isLenderDashboard={isLenderDashboard}
              fetchToken={() => token}
              notify={notify}
              moveApplication={true}
              isMandatory={selectedStage.value === getRejectedKey}
            />
          )}

        <fieldset>
          <TextArea
            rows={5}
            name="remark"
            label="Remarks"
            styles={{
              container: `w-full mt-2 mb-4 md:mr-4 md:mb-0`,
              input: "text-xs placeholder:text-xs",
              label: "text-xs"
            }}
            placeholder={"Write a remark..."}
            register={register("remark")}
            // error={errors.remark}
            // errorText={
            //   errors.remark?.type === "required" ? "remark is required" : ""
            // }
          />
        </fieldset>

        {isHERO_FINCORP && (
          <div className="mt-4">
            <FileUploadInput
              outsideView
              name="file"
              accept={{
                "image/*": [".jpeg", ".png", ".jpg"],
                "application/pdf": [".pdf"],
                "application/zip": [".zip"]
              }}
              styles={{
                inputContainer:
                  "bg-white !h-[130px] !min-h-[130px] !border-solid"
              }}
              maxFiles={maxFilesAllowed}
              onDropAccepted={() => {
                // Do nothing — FileUploadInput's onDrop already sets the full accumulated file array via setValue internally.
                // Overriding here with data[0] would break multi-file.
                // if (!editMode) {
                //   setValue("file", data[0] as any, { shouldValidate: true });
                // }
              }}
              onDropRejected={handleDropRejected}
              register={register("file") as any}
              viewDocument={(file: File) => setObject(file)}
              control={control}
              setValue={setValue}
              isRequired={false}
              required={false}
              showTakePhoto={false}
              disabled={isFileUploadDisabled()}
              label={"Upload Document"}
              customText={renderUploadCustomText()}
            />
          </div>
        )}

        {!isHERO_FINCORP && (
          <fieldset className="mb-7 mt-4">
            <Checkbox
              aria-describedby="backDated-description"
              enabled={allTaskItemsCompleted}
              item={{
                label: "All Task-list Item has been Completed",
                value: "allTaskItemsCompleted"
              }}
              setEnabled={(checked: boolean) =>
                setAllTaskItemsCompleted(checked)
              }
              style={{
                label: "ml-2 text-xs font-medium text-slate-700 text-gray-500",
                checkbox: "w-4 h-4",
                checkboxIconBorder: "border-gray-300"
              }}
            />
          </fieldset>
        )}

        <fieldset className="flex items-center justify-end mt-4 mb-1">
          <button
            className="text-sm px-4 py-2 w-auto rounded-lg text-white bg-indigo-700 flex items-center justify-center font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            type="submit"
            disabled={
              isLoading ||
              isLoadingActions ||
              actions?.length === 0 ||
              !selectedStage
            }
          >
            {isLoading ? (
              <div className="flex flex-row items-center gap-2 justify-center">
                <ArrowPathIcon className="w-4 h-4 animate-spin text-white" />
                Loading...
              </div>
            ) : (
              "Confirm"
            )}
          </button>
        </fieldset>
      </form>
      <UploadedDocumentView
        objectFile={object}
        onClose={() => setObject(null)}
      />
    </div>
  );
}
